<template>
  <div>
    <h2 class="page-title">
      <el-icon><Cpu /></el-icon>
      装置管理
    </h2>
    <el-alert
      v-if="identityPreview"
      title="本地功能预览：这里使用独立测试账号和设备数据，修改不会写入线上数据库。"
      type="warning"
      :closable="false"
      show-icon
      style="margin-bottom: 16px"
    />
    <div class="card">
      <div class="table-operations">
        <div style="display: flex; gap: 10px">
          <el-button type="primary" :icon="Link" @click="handleBindMine"
            >绑定我的设备</el-button
          >
          <el-button
            v-if="isAdminOrAbove"
            type="primary"
            :icon="Plus"
            @click="handleAddDevice"
          >
            添加装置
          </el-button>
          <el-button
            v-if="isAdminOrAbove"
            type="success"
            :icon="Link"
            @click="handleBatchBind"
          >
            批量绑定用户
          </el-button>
        </div>
        <div style="display: flex; gap: 10px">
          <el-input
            placeholder="搜索机器码或机器名"
            style="width: 220px"
            v-model="deviceSearch"
            clearable
          >
            <template #append>
              <el-button :icon="Search"></el-button>
            </template>
          </el-input>
          <el-button :icon="Refresh" @click="fetchDeviceList">刷新</el-button>
          <el-button
            type="warning"
            :icon="Connection"
            :loading="checkingStatus"
            @click="handleCheckOnlineStatus"
          >
            检测状态
          </el-button>
          <el-button
            type="info"
            :icon="Location"
            :loading="updatingGps"
            @click="handleUpdateGps"
          >
            更新GPS
          </el-button>
        </div>
      </div>

      <el-table
        :data="paginatedDeviceList"
        stripe
        style="width: 100%"
        v-loading="loading"
      >
        <el-table-column label="机器码" min-width="190" align="center">
          <template #default="{ row }">
            <div>{{ row.machineCode || row.id }}</div>
            <small
              v-if="row.machineCode && row.machineCode !== row.id"
              style="color: #909399"
              >原设备编号：{{ row.id }}</small
            >
            <el-button
              v-if="isAdminOrAbove && !row.machineCode"
              link
              type="primary"
              @click="handleRegisterCode(row)"
              >登记机器码</el-button
            >
          </template>
        </el-table-column>
        <el-table-column prop="deviceName" label="机器名" min-width="150">
          <template #default="{ row }">
            <span>{{ row.deviceName }}</span>
            <el-button
              v-if="row.canRename"
              link
              type="primary"
              style="margin-left: 10px"
              @click="handleRename(row)"
              >改名</el-button
            >
          </template>
        </el-table-column>
        <el-table-column label="固件版本" width="115" align="center">
          <template #default="{ row }">
            {{ row.firmwareVersion || "未上报" }}
          </template>
        </el-table-column>
        <el-table-column prop="status" label="状态" width="100" align="center">
          <template #default="scope">
            <el-tag
              v-if="scope.row.status === 'online' || scope.row.status === 1"
              type="success"
              effect="dark"
            >
              <el-icon><SuccessFilled /></el-icon>
              在线
            </el-tag>
            <el-tag v-else type="danger" effect="dark">
              <el-icon><WarningFilled /></el-icon>
              离线
            </el-tag>
          </template>
        </el-table-column>
        <el-table-column
          prop="pondName"
          label="所属虾塘"
          width="150"
        ></el-table-column>
        <el-table-column prop="createTime" label="创建时间" width="180">
          <template #default="scope">
            {{ formatDate(scope.row.createdTime || scope.row.createTime) }}
          </template>
        </el-table-column>
        <el-table-column label="操作" width="420" fixed="right" align="center">
          <template #default="scope">
            <el-button
              size="small"
              :icon="View"
              @click="handleViewDetail(scope.row)"
              >详情</el-button
            >
            <el-button
              size="small"
              type="primary"
              :icon="Clock"
              @click="handleCaptureSchedule(scope.row)"
              >定时拍照</el-button
            >
            <el-button
              size="small"
              type="success"
              :icon="VideoCamera"
              @click="handleVideoSchedule(scope.row)"
              >定时录制</el-button
            >
            <el-button
              size="small"
              type="danger"
              :icon="Delete"
              @click="handleDelete(scope.row)"
              >{{ isAdminOrAbove ? "删除" : "解绑" }}</el-button
            >
          </template>
        </el-table-column>
      </el-table>

      <div class="pagination">
        <el-pagination
          v-model:current-page="currentPage"
          v-model:page-size="pageSize"
          :page-sizes="[5, 10, 20, 50]"
          :total="total"
          layout="total, sizes, prev, pager, next, jumper"
          @size-change="handleSizeChange"
          @current-change="handleCurrentChange"
        ></el-pagination>
      </div>
    </div>

    <!-- 添加装置对话框 -->
    <el-dialog
      v-model="addDialogVisible"
      title="添加装置"
      width="500px"
      :close-on-click-modal="false"
    >
      <el-form
        :model="addForm"
        :rules="addRules"
        ref="addFormRef"
        label-width="100px"
      >
        <el-form-item label="机器码" prop="id">
          <el-input
            v-model="addForm.id"
            placeholder="请输入设备唯一机器码"
          ></el-input>
        </el-form-item>
        <el-form-item label="设备备注" prop="deviceName">
          <el-input
            v-model="addForm.deviceName"
            placeholder="可选；用户绑定后会自动生成自己的机器名"
          ></el-input>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="addDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="handleSubmitAdd"
          :loading="submitting"
        >
          确定
        </el-button>
      </template>
    </el-dialog>

    <!-- 批量绑定用户对话框 -->
    <el-dialog
      v-model="bindDialogVisible"
      title="批量绑定设备与用户"
      width="550px"
      :close-on-click-modal="false"
    >
      <el-form
        :model="bindForm"
        :rules="bindRules"
        ref="bindFormRef"
        label-width="100px"
      >
        <el-form-item label="用户手机号" prop="phone">
          <el-input
            v-model="bindForm.phone"
            placeholder="请输入要绑定的用户手机号"
          ></el-input>
        </el-form-item>
        <el-form-item label="选择设备" prop="deviceIds">
          <el-select
            v-model="bindForm.deviceIds"
            multiple
            filterable
            placeholder="请选择要绑定的设备"
            style="width: 100%"
          >
            <el-option
              v-for="device in deviceList"
              :key="device.id"
              :label="`${device.deviceName || device.id} (${device.id})`"
              :value="device.id"
            ></el-option>
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="bindDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="handleSubmitBind"
          :loading="bindSubmitting"
        >
          确定绑定
        </el-button>
      </template>
    </el-dialog>

    <!-- 定时拍照配置对话框 -->
    <el-dialog
      v-model="captureDialogVisible"
      title="定时拍照配置"
      width="550px"
      :close-on-click-modal="false"
    >
      <el-form label-width="100px" v-loading="captureLoading">
        <el-form-item label="设备">
          <span>{{ captureForm.deviceId }}</span>
        </el-form-item>
        <el-form-item label="启用定时">
          <el-switch v-model="captureForm.enabled" />
        </el-form-item>
        <el-form-item label="拍照时间点">
          <div class="time-list">
            <el-tag
              v-for="(time, index) in captureForm.times"
              :key="index"
              closable
              @close="removeTime(index)"
              style="margin-right: 8px; margin-bottom: 8px"
            >
              {{ time }}
            </el-tag>
          </div>
          <div style="display: flex; gap: 10px; margin-top: 10px">
            <el-time-picker
              v-model="newTime"
              format="HH:mm"
              value-format="HH:mm"
              placeholder="选择时间"
              style="width: 150px"
            />
            <el-button type="primary" :icon="Plus" @click="addTime">
              添加时间点
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="上次触发">
          <span>{{
            captureForm.lastFireAt ? formatDate(captureForm.lastFireAt) : "暂无"
          }}</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="captureDialogVisible = false">取消</el-button>
        <el-button
          type="primary"
          @click="handleSaveCaptureSchedule"
          :loading="captureSaving"
        >
          保存
        </el-button>
      </template>
    </el-dialog>

    <!-- 定时录制视频配置对话框 -->
    <el-dialog
      v-model="videoDialogVisible"
      title="定时录制视频配置"
      width="550px"
      :close-on-click-modal="false"
    >
      <el-form label-width="110px" v-loading="videoLoading">
        <el-form-item label="设备">
          <span>{{ videoForm.deviceId }}</span>
        </el-form-item>
        <el-form-item label="启用定时">
          <el-switch v-model="videoForm.enabled" />
        </el-form-item>
        <el-form-item label="录制时间点">
          <div class="time-list">
            <el-tag
              v-for="(time, index) in videoForm.times"
              :key="index"
              closable
              type="success"
              @close="removeVideoTime(index)"
              style="margin-right: 8px; margin-bottom: 8px"
            >
              {{ time }}
            </el-tag>
          </div>
          <div style="display: flex; gap: 10px; margin-top: 10px">
            <el-time-picker
              v-model="newVideoTime"
              format="HH:mm"
              value-format="HH:mm"
              placeholder="选择时间"
              style="width: 150px"
            />
            <el-button type="success" :icon="Plus" @click="addVideoTime">
              添加时间点
            </el-button>
          </div>
        </el-form-item>
        <el-form-item label="录制时长">
          <span>每次固定录制 30 秒 MP4 视频</span>
        </el-form-item>
        <el-form-item label="上次触发">
          <span>{{
            videoForm.lastFireAt ? formatDate(videoForm.lastFireAt) : "暂无"
          }}</span>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="videoDialogVisible = false">取消</el-button>
        <el-button
          type="success"
          @click="handleSaveVideoSchedule"
          :loading="videoSaving"
        >
          保存
        </el-button>
      </template>
    </el-dialog>

    <!-- 设备详情对话框 -->
    <el-dialog v-model="detailDialogVisible" title="设备详情" width="500px">
      <el-descriptions :column="1" border v-if="currentDevice">
        <el-descriptions-item label="机器码">
          {{ currentDevice.machineCode || currentDevice.id }}
        </el-descriptions-item>
        <el-descriptions-item
          v-if="
            currentDevice.machineCode &&
            currentDevice.machineCode !== currentDevice.id
          "
          label="原设备编号"
        >
          {{ currentDevice.id }}
        </el-descriptions-item>
        <el-descriptions-item label="机器名">
          {{ currentDevice.deviceName }}
        </el-descriptions-item>
        <el-descriptions-item label="固件版本">
          {{ currentDevice.firmwareVersion || "未上报" }}
        </el-descriptions-item>
        <el-descriptions-item label="SIM 卡 ICCID">
          {{
            currentDevice.iccid ||
            (currentDevice.iccidAvailable === false
              ? "设备未读取到 SIM 卡"
              : "未上报")
          }}
        </el-descriptions-item>
        <el-descriptions-item label="机器信息上报时间">
          {{
            currentDevice.machineReportedAt
              ? formatDate(currentDevice.machineReportedAt)
              : "未上报"
          }}
        </el-descriptions-item>
        <el-descriptions-item label="设备类型">
          {{ currentDevice.deviceType || "未知" }}
        </el-descriptions-item>
        <el-descriptions-item label="状态">
          <el-tag
            :type="
              currentDevice.status === 'online' || currentDevice.status === 1
                ? 'success'
                : 'danger'
            "
          >
            {{
              currentDevice.status === "online" || currentDevice.status === 1
                ? "在线"
                : "离线"
            }}
          </el-tag>
        </el-descriptions-item>
        <el-descriptions-item label="所属虾塘">
          {{ currentDevice.pondName || "-" }}
        </el-descriptions-item>
        <el-descriptions-item label="坐标信息">
          {{ currentDevice.coordinate || "-" }}
        </el-descriptions-item>
        <el-descriptions-item label="温度">
          {{
            currentDevice.temperature != null
              ? `${currentDevice.temperature} °C`
              : "-"
          }}
        </el-descriptions-item>
        <el-descriptions-item label="创建时间">
          {{
            formatDate(currentDevice.createdTime || currentDevice.createTime)
          }}
        </el-descriptions-item>
      </el-descriptions>
      <template #footer>
        <el-button @click="detailDialogVisible = false">关闭</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script>
import { ref, reactive, computed, onMounted, watch } from "vue";
import { useStore } from "vuex";
import { ElMessage, ElMessageBox } from "element-plus";
import * as ElementPlusIconsVue from "@element-plus/icons-vue";
import {
  Cpu,
  SuccessFilled,
  WarningFilled,
  Plus,
  Link,
  Connection,
  Location,
  Clock,
  VideoCamera,
} from "@element-plus/icons-vue";
import {
  listAllDevices,
  listDevicesByUserId,
  removeDevice,
  getDeviceInfo,
  addDevice,
  batchBindDeviceUser,
  checkDevicesOnline,
  batchGetDevicesLocation,
  getCaptureSchedule,
  saveCaptureSchedule,
  getVideoSchedule,
  saveVideoSchedule,
  renameMyDevice,
  registerMachineCode,
  bindMyDevice,
  deviceIdentitySettings,
  unbindMyDevice,
} from "@/api/device";

export default {
  name: "DeviceList",
  setup() {
    const store = useStore();
    const Search = ElementPlusIconsVue.Search;
    const Delete = ElementPlusIconsVue.Delete;
    const View = ElementPlusIconsVue.View;
    const Refresh = ElementPlusIconsVue.Refresh;

    const deviceSearch = ref("");
    const loading = ref(false);
    const checkingStatus = ref(false);
    const updatingGps = ref(false);
    const submitting = ref(false);
    const currentPage = ref(1);
    const pageSize = ref(10);
    const total = ref(0);
    const deviceList = ref([]);
    const detailDialogVisible = ref(false);
    const addDialogVisible = ref(false);
    const addFormRef = ref(null);
    const currentDevice = ref(null);
    const identityPreview = ref(false);

    // 添加装置表单
    const addForm = reactive({
      id: "",
      deviceName: "",
    });

    // 添加装置表单验证规则
    const addRules = reactive({
      id: [{ required: true, message: "请输入机器码", trigger: "blur" }],
    });

    // 批量绑定相关
    const bindDialogVisible = ref(false);
    const bindFormRef = ref(null);
    const bindSubmitting = ref(false);

    const bindForm = reactive({
      phone: "",
      deviceIds: [],
    });

    const bindRules = reactive({
      phone: [
        { required: true, message: "请输入用户手机号", trigger: "blur" },
        {
          pattern: /^1[3-9]\d{9}$/,
          message: "请输入正确的手机号码",
          trigger: "blur",
        },
      ],
      deviceIds: [
        {
          required: true,
          message: "请选择要绑定的设备",
          trigger: "change",
          type: "array",
          min: 1,
        },
      ],
    });

    // 定时拍照配置相关
    const captureDialogVisible = ref(false);
    const captureLoading = ref(false);
    const captureSaving = ref(false);
    const newTime = ref(null);
    const captureForm = reactive({
      deviceId: "",
      enabled: false,
      times: [],
      lastFireAt: null,
    });

    // 定时录制视频配置相关
    const videoDialogVisible = ref(false);
    const videoLoading = ref(false);
    const videoSaving = ref(false);
    const newVideoTime = ref(null);
    const videoForm = reactive({
      deviceId: "",
      enabled: false,
      times: [],
      lastFireAt: null,
    });

    // 判断是否是管理员及以上权限
    const isAdminOrAbove = computed(() => store.getters.isAdminOrAbove);

    // 过滤后的设备列表
    const filteredDeviceList = computed(() => {
      if (!deviceSearch.value) {
        return deviceList.value;
      }
      const searchText = deviceSearch.value.toLowerCase();
      return deviceList.value.filter(
        (device) =>
          (device.id && device.id.toLowerCase().includes(searchText)) ||
          (device.machineCode &&
            device.machineCode.toLowerCase().includes(searchText)) ||
          (device.deviceName &&
            device.deviceName.toLowerCase().includes(searchText)) ||
          (device.deviceType &&
            device.deviceType.toLowerCase().includes(searchText)) ||
          (device.pondName &&
            device.pondName.toLowerCase().includes(searchText))
      );
    });

    // 分页后的设备列表
    const paginatedDeviceList = computed(() => {
      const start = (currentPage.value - 1) * pageSize.value;
      const end = start + pageSize.value;
      return filteredDeviceList.value.slice(start, end);
    });
    watch(filteredDeviceList, (rows) => {
      total.value = rows.length;
      currentPage.value = 1;
    });

    // 格式化日期
    const formatDate = (dateStr) => {
      if (!dateStr) return "-";
      const normalizedDateStr =
        typeof dateStr === "string" ? dateStr.replace(" ", "T") : dateStr;
      const date = new Date(normalizedDateStr);
      if (Number.isNaN(date.getTime())) {
        return typeof dateStr === "string" ? dateStr.replace("T", " ") : "-";
      }
      return date.toLocaleString("zh-CN", {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
      });
    };

    // 获取设备列表
    const fetchDeviceList = async () => {
      loading.value = true;
      try {
        let res;
        // 调试日志
        const user = store.state.user;
        console.log("当前用户信息:", user);
        console.log("用户角色:", store.getters.userRole);
        console.log("isAdminOrAbove:", isAdminOrAbove.value);

        // 管理员及以上权限调用 list 接口，否则调用 listByUserId
        if (isAdminOrAbove.value) {
          console.log("调用 listAllDevices 接口");
          res = await listAllDevices();
        } else {
          console.log("调用 listDevicesByUserId 接口");
          res = await listDevicesByUserId();
        }

        if (res.code === 0) {
          deviceList.value = res.data || [];
          total.value = deviceList.value.length;
        } else {
          ElMessage.error(res.message || "获取设备列表失败");
        }
      } catch (error) {
        console.error("获取设备列表异常:", error);
        ElMessage.error("获取设备列表失败");
      } finally {
        loading.value = false;
      }
    };

    // 批量检测设备在线状态
    const checkDevicesOnlineStatus = async () => {
      try {
        const deviceIds = deviceList.value.map((device) => device.id);
        console.log("批量检测设备在线状态，设备IDs:", deviceIds);
        const res = await checkDevicesOnline(deviceIds);
        console.log("批量检测设备在线状态返回:", res);
        if (res && res.devices) {
          // 更新设备列表中的在线状态
          res.devices.forEach((item) => {
            const device = deviceList.value.find((d) => d.id === item.deviceId);
            if (device) {
              device.status = item.online ? 1 : 0;
            }
          });
        }
      } catch (error) {
        console.error("批量检测设备在线状态异常:", error);
      }
    };

    // 手动检测设备在线状态
    const handleCheckOnlineStatus = async () => {
      if (deviceList.value.length === 0) {
        ElMessage.warning("没有设备可检测");
        return;
      }
      checkingStatus.value = true;
      try {
        await checkDevicesOnlineStatus();
        ElMessage.success("设备状态检测完成");
      } finally {
        checkingStatus.value = false;
      }
    };

    // 更新当前页面设备GPS坐标
    const handleUpdateGps = async () => {
      const currentDevices = paginatedDeviceList.value;
      if (currentDevices.length === 0) {
        ElMessage.warning("当前页面没有设备");
        return;
      }
      updatingGps.value = true;
      try {
        const deviceIds = currentDevices.map((device) => device.id);
        console.log("批量更新设备GPS，设备IDs:", deviceIds);
        const res = await batchGetDevicesLocation(deviceIds);
        console.log("批量获取设备GPS返回:", res);

        if (res && res.devices) {
          let successCount = 0;
          let failCount = 0;

          res.devices.forEach((item) => {
            const device = deviceList.value.find((d) => d.id === item.deviceId);
            if (device && item.coordinate) {
              // 成功：获取到了坐标
              device.coordinate = item.coordinate;
              device.updatedTime = item.updatedTime;
              successCount++;
            } else {
              // 失败：不管是什么原因（没有坐标或有错误），都算为失败
              failCount++;
            }
          });

          if (successCount > 0) {
            ElMessage.success(
              `GPS更新完成，成功: ${successCount}，失败: ${failCount}`
            );
          } else {
            ElMessage.warning("没有设备成功获取GPS坐标");
          }
        } else {
          ElMessage.error("获取GPS坐标失败");
        }
      } catch (error) {
        console.error("批量更新设备GPS异常:", error);
        ElMessage.error("更新GPS坐标失败");
      } finally {
        updatingGps.value = false;
      }
    };

    // 查看设备详情
    const handleViewDetail = async (row) => {
      try {
        const res = await getDeviceInfo(row.id);
        if (res.code === 0) {
          currentDevice.value = res.data;
          detailDialogVisible.value = true;
        } else {
          ElMessage.error(res.message || "获取设备详情失败");
        }
      } catch (error) {
        console.error("获取设备详情异常:", error);
        ElMessage.error("获取设备详情失败");
      }
    };

    const handleRename = async (row) => {
      try {
        const { value } = await ElMessageBox.prompt(
          "只修改你自己看到的机器名，设备数据关联保持不变。",
          "修改机器名",
          {
            inputValue: row.deviceName,
            confirmButtonText: "保存",
            cancelButtonText: "取消",
            inputValidator: (value) =>
              (value && value.trim() && [...value.trim()].length <= 64) ||
              "请输入1至64个字符的机器名",
          }
        );
        const result = await renameMyDevice(row.id, value.trim());
        if (result.code !== 0) throw new Error(result.message);
        ElMessage.success("机器名已保存");
        await fetchDeviceList();
      } catch (error) {
        if (error !== "cancel" && error !== "close")
          ElMessage.error(error.message || "保存失败");
      }
    };
    const handleBindMine = async () => {
      try {
        const { value } = await ElMessageBox.prompt(
          "输入设备二维码中的机器码。绑定后会自动生成001、002等机器名，也可以自行改名。",
          "绑定我的设备",
          {
            confirmButtonText: "绑定",
            cancelButtonText: "取消",
            inputValidator: (value) => !!value?.trim() || "请输入机器码",
          }
        );
        const result = await bindMyDevice(value.trim());
        if (result.code !== 0) throw new Error(result.message);
        ElMessage.success(`绑定成功，机器名：${result.data.machineName}`);
        await fetchDeviceList();
      } catch (error) {
        if (error !== "cancel" && error !== "close")
          ElMessage.error(error.message || "绑定失败");
      }
    };
    const handleRegisterCode = async (row) => {
      try {
        const { value } = await ElMessageBox.prompt(
          `为原设备${row.id}登记硬件机器码。登记后不可随意修改，请先核对设备。`,
          "登记机器码",
          {
            confirmButtonText: "保存登记",
            cancelButtonText: "取消",
            inputValidator: (value) => !!value?.trim() || "请输入机器码",
          }
        );
        const result = await registerMachineCode(row.id, value.trim());
        if (result.code !== 0) throw new Error(result.message);
        ElMessage.success("机器码已登记，历史数据关联保持不变");
        await fetchDeviceList();
      } catch (error) {
        if (error !== "cancel" && error !== "close")
          ElMessage.error(error.message || "登记失败");
      }
    };

    // 删除设备
    const handleDelete = (row) => {
      ElMessageBox.confirm(
        `确定要${isAdminOrAbove.value ? "删除" : "解绑"}设备 "${
          row.deviceName || row.id
        }" 吗？`,
        "提示",
        {
          confirmButtonText: "确定",
          cancelButtonText: "取消",
          type: "warning",
        }
      )
        .then(async () => {
          try {
            const res = await (isAdminOrAbove.value
              ? removeDevice(row.id)
              : unbindMyDevice(row.id));
            if (res.code === 0) {
              ElMessage.success(isAdminOrAbove.value ? "删除成功" : "解绑成功");
              fetchDeviceList(); // 刷新列表
            } else {
              ElMessage.error(res.message || "删除失败");
            }
          } catch (error) {
            console.error("删除设备异常:", error);
            ElMessage.error("删除设备失败");
          }
        })
        .catch(() => {});
    };

    // 打开添加装置对话框
    const handleAddDevice = () => {
      // 重置表单
      addForm.id = "";
      addForm.deviceName = "";
      addDialogVisible.value = true;
    };

    // 提交添加装置
    const handleSubmitAdd = () => {
      if (!addFormRef.value) return;

      addFormRef.value.validate(async (valid) => {
        if (valid) {
          submitting.value = true;
          try {
            const deviceData = {
              id: addForm.id,
              machineCode: addForm.id,
              deviceName: addForm.deviceName || addForm.id,
            };

            const res = await addDevice(deviceData);
            if (res.code === 0) {
              ElMessage.success("添加装置成功");
              addDialogVisible.value = false;
              fetchDeviceList(); // 刷新列表
            } else {
              ElMessage.error(res.message || "添加装置失败");
            }
          } catch (error) {
            console.error("添加装置异常:", error);
            ElMessage.error("添加装置失败");
          } finally {
            submitting.value = false;
          }
        }
      });
    };

    // 打开批量绑定对话框
    const handleBatchBind = () => {
      bindForm.phone = "";
      bindForm.deviceIds = [];
      bindDialogVisible.value = true;
    };

    // 提交批量绑定
    const handleSubmitBind = () => {
      if (!bindFormRef.value) return;

      bindFormRef.value.validate(async (valid) => {
        if (valid) {
          bindSubmitting.value = true;
          try {
            const res = await batchBindDeviceUser({
              phone: bindForm.phone,
              deviceIds: bindForm.deviceIds,
            });

            if (res.code === 0) {
              ElMessage.success("批量绑定成功");
              bindDialogVisible.value = false;
              fetchDeviceList();
            } else {
              ElMessage.error(res.message || "批量绑定失败");
            }
          } catch (error) {
            console.error("批量绑定异常:", error);
            ElMessage.error("批量绑定失败");
          } finally {
            bindSubmitting.value = false;
          }
        }
      });
    };

    // 打开定时拍照配置对话框
    const handleCaptureSchedule = async (row) => {
      captureForm.deviceId = row.id;
      captureForm.enabled = false;
      captureForm.times = [];
      captureForm.lastFireAt = null;
      newTime.value = null;
      captureDialogVisible.value = true;
      captureLoading.value = true;

      try {
        const res = await getCaptureSchedule(row.id);
        if (res.code === 0 && res.data) {
          captureForm.enabled = res.data.enabled || false;
          captureForm.times = res.data.times || [];
          captureForm.lastFireAt = res.data.lastFireAt;
        }
      } catch (error) {
        console.error("获取定时拍照配置异常:", error);
        ElMessage.error("获取配置失败");
      } finally {
        captureLoading.value = false;
      }
    };

    // 添加时间点
    const addTime = () => {
      if (!newTime.value) {
        ElMessage.warning("请先选择时间");
        return;
      }
      if (captureForm.times.includes(newTime.value)) {
        ElMessage.warning("该时间点已存在");
        return;
      }
      captureForm.times.push(newTime.value);
      captureForm.times.sort();
      newTime.value = null;
    };

    // 移除时间点
    const removeTime = (index) => {
      captureForm.times.splice(index, 1);
    };

    // 保存定时拍照配置
    const handleSaveCaptureSchedule = async () => {
      if (captureForm.enabled && captureForm.times.length === 0) {
        ElMessage.warning("开启定时拍照需要至少配置一个时间点");
        return;
      }

      captureSaving.value = true;
      try {
        const res = await saveCaptureSchedule({
          deviceId: captureForm.deviceId,
          enabled: captureForm.enabled,
          times: captureForm.times,
        });

        if (res.code === 0) {
          ElMessage.success("保存成功");
          captureDialogVisible.value = false;
        } else {
          ElMessage.error(res.message || "保存失败");
        }
      } catch (error) {
        console.error("保存定时拍照配置异常:", error);
        ElMessage.error("保存失败");
      } finally {
        captureSaving.value = false;
      }
    };

    // 打开定时录制视频配置对话框
    const handleVideoSchedule = async (row) => {
      videoForm.deviceId = row.id;
      videoForm.enabled = false;
      videoForm.times = [];
      videoForm.lastFireAt = null;
      newVideoTime.value = null;
      videoDialogVisible.value = true;
      videoLoading.value = true;

      try {
        const res = await getVideoSchedule(row.id);
        if (res.code === 0 && res.data) {
          videoForm.enabled = res.data.enabled || false;
          videoForm.times = res.data.times || [];
          videoForm.lastFireAt = res.data.lastFireAt;
        } else {
          ElMessage.error(res.message || "获取定时录制配置失败");
        }
      } catch (error) {
        console.error("获取定时录制视频配置异常:", error);
        ElMessage.error("获取配置失败");
      } finally {
        videoLoading.value = false;
      }
    };

    // 添加定时录制时间点
    const addVideoTime = () => {
      if (!newVideoTime.value) {
        ElMessage.warning("请先选择时间");
        return;
      }
      if (videoForm.times.includes(newVideoTime.value)) {
        ElMessage.warning("该时间点已存在");
        return;
      }
      videoForm.times.push(newVideoTime.value);
      videoForm.times.sort();
      newVideoTime.value = null;
    };

    const removeVideoTime = (index) => {
      videoForm.times.splice(index, 1);
    };

    // 保存定时录制视频配置
    const handleSaveVideoSchedule = async () => {
      if (videoForm.enabled && videoForm.times.length === 0) {
        ElMessage.warning("开启定时录制视频需要至少配置一个时间点");
        return;
      }

      videoSaving.value = true;
      try {
        const res = await saveVideoSchedule({
          deviceId: videoForm.deviceId,
          enabled: videoForm.enabled,
          times: videoForm.times,
        });

        if (res.code === 0) {
          ElMessage.success("定时录制配置保存成功");
          videoDialogVisible.value = false;
        } else {
          ElMessage.error(res.message || "保存失败");
        }
      } catch (error) {
        console.error("保存定时录制视频配置异常:", error);
        ElMessage.error("保存失败");
      } finally {
        videoSaving.value = false;
      }
    };

    const handleSizeChange = (val) => {
      pageSize.value = val;
      currentPage.value = 1;
    };

    const handleCurrentChange = (val) => {
      currentPage.value = val;
    };

    const userLoaded = ref(false);

    watch(
      () => store.state.user,
      (newUser) => {
        if (newUser && !userLoaded.value) {
          userLoaded.value = true;
          fetchDeviceList();
        }
      },
      { immediate: true }
    );

    onMounted(() => {
      deviceIdentitySettings()
        .then((result) => {
          identityPreview.value = !!result.data?.preview;
        })
        .catch(() => {});
      if (store.state.user && !userLoaded.value) {
        userLoaded.value = true;
        fetchDeviceList();
      }
    });

    return {
      Cpu,
      SuccessFilled,
      WarningFilled,
      Plus,
      Link,
      Connection,
      Location,
      Clock,
      VideoCamera,
      Search,
      Delete,
      View,
      Refresh,
      deviceSearch,
      loading,
      checkingStatus,
      updatingGps,
      submitting,
      bindSubmitting,
      deviceList,
      filteredDeviceList,
      paginatedDeviceList,
      currentPage,
      pageSize,
      total,
      detailDialogVisible,
      addDialogVisible,
      addFormRef,
      addForm,
      addRules,
      bindDialogVisible,
      bindFormRef,
      bindForm,
      bindRules,
      currentDevice,
      identityPreview,
      handleRename,
      handleBindMine,
      handleRegisterCode,
      isAdminOrAbove,
      captureDialogVisible,
      captureLoading,
      captureSaving,
      captureForm,
      newTime,
      videoDialogVisible,
      videoLoading,
      videoSaving,
      videoForm,
      newVideoTime,
      formatDate,
      fetchDeviceList,
      handleAddDevice,
      handleSubmitAdd,
      handleBatchBind,
      handleSubmitBind,
      handleViewDetail,
      handleDelete,
      handleCheckOnlineStatus,
      handleUpdateGps,
      handleSizeChange,
      handleCurrentChange,
      handleCaptureSchedule,
      addTime,
      removeTime,
      handleSaveCaptureSchedule,
      handleVideoSchedule,
      addVideoTime,
      removeVideoTime,
      handleSaveVideoSchedule,
    };
  },
};
</script>

<style scoped>
.page-title {
  font-size: 20px;
  margin-bottom: 20px;
  color: #1b9a7c;
  padding-bottom: 10px;
  border-bottom: 1px solid #e0e0e0;
  display: flex;
  align-items: center;
  gap: 8px;
}

.page-title .el-icon {
  font-size: 24px;
}

.table-operations {
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 20px;
  display: flex;
  justify-content: space-between;
  align-items: center;
}

.pagination {
  margin-top: 20px;
  text-align: right;
}

.card {
  background: white;
  border-radius: 4px;
  box-shadow: 0 2px 12px 0 rgba(0, 0, 0, 0.1);
  padding: 20px;
  margin-bottom: 20px;
}
</style>
